import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ViewOnlyDashboard = () => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/analytics/overview`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      setAnalytics(analyticsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analytics/export?format=${format}`, { 
        withCredentials: true 
      });

      if (format === 'csv' && response.data.data) {
        // Create download link
        const blob = new Blob([response.data.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || 'export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      setExporting(false);
    } catch (error) {
      console.error('Export error:', error);
      setExporting(false);
      alert('Failed to export data');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <img 
                src="/logo.png" 
                alt="Altrunic Logo" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#0A58E5' }}>Altrunic</h1>
                <p className="text-sm text-gray-500">Analytics Dashboard (View Only)</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 bg-gray-200 px-4 py-2 rounded-lg">
                <span className="text-sm font-semibold text-gray-700">View Only Access</span>
              </div>
              <div className="hidden md:flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg">
                <span className="text-sm text-gray-600">Welcome,</span>
                <span className="text-sm font-semibold text-gray-900">{user?.name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Export Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Export Reports</h2>
                <p className="text-gray-600 mt-1">Download analytics and donation data</p>
              </div>
              <button
                onClick={() => handleExport('csv')}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center space-x-2"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <span>📥</span>
                    <span>Export CSV</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Total Raised</h3>
                <span className="text-3xl">💰</span>
              </div>
              <p className="text-4xl font-bold mb-1">${(analytics?.total_raised || 0).toLocaleString()}</p>
              <div className="text-sm opacity-75">All time</div>
            </div>

            <div className="bg-gradient-to-br from-blue-400 to-blue-500 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Active Campaigns</h3>
                <span className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">C</span>
              </div>
              <p className="text-4xl font-bold mb-1">{analytics?.active_campaigns || 0}</p>
              <div className="text-sm opacity-75">Currently running</div>
            </div>

            <div className="bg-gradient-to-br from-gray-600 to-gray-700 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Total Impact</h3>
                <span className="text-3xl">💙</span>
              </div>
              <p className="text-4xl font-bold mb-1">{(analytics?.total_impact || 0).toLocaleString()}</p>
              <div className="text-sm opacity-75">Lives impacted</div>
            </div>

            <div className="bg-gradient-to-br from-slate-500 to-slate-600 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium opacity-90">Avg. Donation</h3>
                <span className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">A</span>
              </div>
              <p className="text-4xl font-bold mb-1">${Math.round(analytics?.avg_donation || 0)}</p>
              <div className="text-sm opacity-75">Per donation</div>
            </div>
          </div>

          {/* Monthly Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Monthly Donations</h3>
            <div className="h-64 flex items-end justify-between space-x-2">
              {analytics?.monthly_data?.map((data, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div className="relative w-full">
                    <div
                      className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all hover:from-blue-600 hover:to-blue-500"
                      style={{ height: `${Math.max((data.amount / 30000) * 100, 5)}%`, minHeight: '20px' }}
                    >
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700">
                        ${(data.amount / 1000).toFixed(0)}k
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-600 mt-2">{data.month}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Campaign Performance */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Campaign Performance</h3>
            {analytics?.campaigns && analytics.campaigns.length > 0 ? (
              <div className="space-y-4">
                {analytics.campaigns.map((campaign) => (
                  <div key={campaign.campaign_id} className="border border-gray-200 rounded-xl p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-lg text-gray-900">{campaign.title}</h4>
                        <p className="text-sm text-gray-500">{campaign.charity_name}</p>
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                        {campaign.status}
                      </span>
                    </div>

                    <div className="mb-3">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-semibold text-gray-900">
                          ${campaign.raised_amount.toLocaleString()} / ${campaign.goal_amount.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min((campaign.raised_amount / campaign.goal_amount) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Impact: {Math.floor(campaign.raised_amount * campaign.impact_conversion_rate)} {campaign.impact_metric}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No campaigns available
              </div>
            )}
          </div>

          {/* Key Metrics */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Key Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {analytics?.total_donations || 0}
                </div>
                <div className="text-sm text-gray-600">Total Donations</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {analytics?.active_campaigns || 0}
                </div>
                <div className="text-sm text-gray-600">Active Campaigns</div>
              </div>
              <div className="text-center p-6 bg-gray-100 rounded-xl">
                <div className="text-3xl font-bold text-gray-700 mb-2">
                  ${(analytics?.total_raised || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Raised</div>
              </div>
            </div>
          </div>

          {/* Read-Only Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">ℹ️</span>
              <div>
                <h4 className="font-semibold text-blue-800">View-Only Access</h4>
                <p className="text-sm text-blue-700 mt-1">
                  You have read-only access to analytics and reports. Contact your Business Admin to request additional permissions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ViewOnlyDashboard;
